import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { userEndpoint, Base_URL } from "actions/API_endpoint";
import Swal from "sweetalert2";
import Select from "react-select";

export default function AddProject() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const [loading, setLoading] = useState(false);
    const [students, setStudents] = useState([]);

    const [formData, setFormData] = useState({
        project_name: "",
        scheme: "ipd",
        no_of_team_members: 1,
        team_leader: "",
        team_members: [],
        mentor_name: "",
        Semester_Registered: "",
        Academic_Year: "",
    });

    useEffect(() => {
        const fetchStudents = async () => {
            try {
                const response = await fetch(`${Base_URL}${userEndpoint.getAllUsers}`);
                const resData = await response.json();
                if (resData.success && resData.data) {
                    setStudents(resData.data.filter(u => u.role === 'student'));
                }
            } catch (error) {
                console.error("Error fetching students:", error);
            }
        };
        fetchStudents();
    }, []);

    useEffect(() => {
        if (isEditMode) {
            const fetchProject = async () => {
                try {
                    const response = await fetch(`${Base_URL}${userEndpoint.getProjectById(id)}`);
                    const data = await response.json();
                    if (data.success) {
                        const proj = data.data;
                        let members = [];
                        if (Array.isArray(proj.team_members)) members = proj.team_members;
                        else if (typeof proj.team_members === 'string') {
                            try { members = JSON.parse(proj.team_members); } catch (e) { }
                        }

                        setFormData({
                            project_name: proj.project_name || "",
                            scheme: proj.scheme || "ipd",
                            no_of_team_members: proj.no_of_team_members || 1,
                            team_leader: proj.team_leader ? proj.team_leader.toString() : "",
                            team_members: members.map(m => m.toString()),
                            mentor_name: proj.mentor_name || "",
                            Semester_Registered: proj.Semester_Registered || "",
                            Academic_Year: proj.Academic_Year || "",
                        });
                    }
                } catch (error) {
                    console.error("Error fetching project:", error);
                }
            };
            fetchProject();
        }
    }, [id, isEditMode]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "team_leader") {
            setFormData(prev => ({
                ...prev,
                team_leader: value,
                team_members: prev.team_members.filter(id => id !== value)
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleMemberAdd = (e) => {
        const value = e.target.value;
        if (!value) return;
        setFormData(prev => ({
            ...prev,
            team_members: [...prev.team_members, value]
        }));
    };

    const removeMember = (idToRemove) => {
        setFormData(prev => ({
            ...prev,
            team_members: prev.team_members.filter(id => id !== idToRemove)
        }));
    };

    const studentOptions = students.map(s => ({
        value: s.user_id.toString(),
        label: `${s.first_name} ${s.last_name}, ${s.admission_no}, ${s.email}`
    }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const submitData = {
                ...formData,
                no_of_team_members: parseInt(formData.no_of_team_members, 10)
            };

            const url = isEditMode
                ? `${Base_URL}${userEndpoint.updateProject(id)}`
                : `${Base_URL}${userEndpoint.createProject}`;
            const method = isEditMode ? "PUT" : "POST";

            const response = await fetch(url, {
                method: method,
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(submitData)
            });

            const data = await response.json();
            if (data.success) {
                Swal.fire("Success", isEditMode ? "Project updated successfully" : "Project created successfully", "success");
                navigate("/admin/projects");
            } else {
                Swal.fire("Error", data.error || `Failed to ${isEditMode ? "update" : "create"} project`, "error");
            }
        } catch (error) {
            console.error("Submit Error:", error);
            Swal.fire("Error", "Something went wrong", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-wrap mt-4 justify-center">
            <div className="relative z-10 w-full lg:w-8/12 px-4 shadow-lg rounded bg-dark-secondary text-white py-6 mb-12">
                <div className="rounded-t bg-dark-tertiary mb-4 px-6 py-4">
                    <div className="flex justify-between items-center">
                        <h6 className="text-white text-xl font-semibold">Project Details</h6>
                        <button
                            className="bg-gray-600 hover:bg-gray-500 text-white active:bg-gray-700 font-bold uppercase text-xs px-4 py-2 rounded shadow outline-none focus:outline-none ease-linear transition-all duration-150"
                            type="button"
                            onClick={() => navigate("/admin/projects")}
                        >
                            <i className="fas fa-arrow-left mr-2"></i> Back
                        </button>
                    </div>
                </div>
                <div className="flex-auto px-4 lg:px-10 py-10 pt-0 mt-6">
                    <form onSubmit={handleSubmit}>
                        <div className="flex flex-wrap">
                            <div className="w-full px-4 mb-4">
                                <label className="block uppercase text-gray-300 text-xs font-bold mb-2">Project Name *</label>
                                <input
                                    type="text"
                                    name="project_name"
                                    value={formData.project_name}
                                    onChange={handleChange}
                                    className="border-0 px-3 py-3 placeholder-gray-500 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full"
                                    required
                                />
                            </div>

                            <div className="w-full lg:w-6/12 px-4 mb-4">
                                <label className="block uppercase text-gray-300 text-xs font-bold mb-2">Scheme *</label>
                                <select
                                    name="scheme"
                                    value={formData.scheme}
                                    onChange={handleChange}
                                    className="border-0 px-3 py-3 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full"
                                    required
                                >
                                    <option value="ipd">IPD</option>
                                    <option value="mpdi">MPDI</option>
                                </select>
                            </div>

                            <div className="w-full lg:w-6/12 px-4 mb-4">
                                <label className="block uppercase text-gray-300 text-xs font-bold mb-2">Mentor Name *</label>
                                <input
                                    type="text"
                                    name="mentor_name"
                                    value={formData.mentor_name}
                                    onChange={handleChange}
                                    className="border-0 px-3 py-3 placeholder-gray-500 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full"
                                    required
                                />
                            </div>

                            <div className="w-full lg:w-6/12 px-4 mb-4">
                                <label className="block uppercase text-gray-300 text-xs font-bold mb-2">Semester Registered *</label>
                                <select
                                    name="Semester_Registered"
                                    value={formData.Semester_Registered}
                                    onChange={handleChange}
                                    className="border-0 px-3 py-3 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full"
                                    required
                                >
                                    <option value="">-- Select Semester --</option>
                                    <option value="Monsoon">Monsoon</option>
                                    <option value="Winter">Winter</option>
                                    <option value="Summer">Summer</option>
                                </select>
                            </div>

                            <div className="w-full lg:w-6/12 px-4 mb-4">
                                <label className="block uppercase text-gray-300 text-xs font-bold mb-2">Academic Year *</label>
                                <input
                                    type="text"
                                    name="Academic_Year"
                                    value={formData.Academic_Year}
                                    onChange={handleChange}
                                    placeholder="2026-27"
                                    className="border-0 px-3 py-3 placeholder-gray-500 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full"
                                    required
                                />
                            </div>

                            <div className="w-full lg:w-6/12 px-4 mb-4">
                                <label className="block uppercase text-gray-300 text-xs font-bold mb-2">No. of Team Members *</label>
                                <input
                                    type="number"
                                    name="no_of_team_members"
                                    value={formData.no_of_team_members}
                                    onChange={handleChange}
                                    min="1"
                                    className="border-0 px-3 py-3 placeholder-gray-500 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full"
                                    required
                                />
                            </div>

                            <div className="w-full lg:w-6/12 px-4 mb-4">
                                <label className="block uppercase text-gray-300 text-xs font-bold mb-2">Team Leader *</label>
                                <Select
                                    options={studentOptions}
                                    value={studentOptions.find(opt => opt.value === formData.team_leader) || null}
                                    onChange={(selected) => handleChange({ target: { name: 'team_leader', value: selected ? selected.value : '' } })}
                                    isClearable
                                    placeholder="Name, Admission, Email"
                                    className="react-select-container text-gray-800"
                                    classNamePrefix="react-select"
                                />
                            </div>

                            <div className="w-full px-4 mb-4">
                                <label className="block uppercase text-gray-300 text-xs font-bold mb-2">Team Members (Optional)</label>
                                <Select
                                    isMulti
                                    options={studentOptions.filter(opt => opt.value !== formData.team_leader)}
                                    value={studentOptions.filter(opt => formData.team_members.includes(opt.value))}
                                    onChange={(selectedOptions) => {
                                        setFormData(prev => ({
                                            ...prev,
                                            team_members: selectedOptions ? selectedOptions.map(opt => opt.value) : []
                                        }));
                                    }}
                                    placeholder="Name, Admission, Email"
                                    className="react-select-container text-gray-800"
                                    classNamePrefix="react-select"
                                />
                            </div>
                        </div>

                        <div className="text-center mt-6">
                            <button
                                className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update Project" : "Create Project")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
